/**
 * Frente #6 — derivar la CANTIDAD de un complementario/previo del plano a partir de la geometría
 * YA medida del elemento principal y del contrato que devuelve la IA.
 *
 * La RELACIÓN de derivación (de qué magnitud sale y con qué factor) la da la IA en el propio
 * `PlanoComplementario` (`derivaDe` + `factor` + `dimensionesFaltantes`); aquí NO hay tabla
 * concepto→fórmula ni mapa de secciones/peraltes (CLAUDE.md §11). Esta función solo APLICA:
 *   cantidad = base(derivaDe) × factor
 * y avisa si la cantidad descansa en un supuesto (una dimensión que el plano no dibuja) para que
 * el renglón entre 'revisar' en vez de adivinar en silencio.
 *
 * Es pura y no conoce alto/espesor: recibe las magnitudes del principal YA resueltas por el
 * consumidor (que reusa lo medido del plano/IFC y no re-mide).
 */
import type { PlanoComplementario } from './domain.js';

/** Magnitudes del elemento PRINCIPAL, ya medidas/derivadas (en metros y unidades métricas). */
export interface MagnitudesPrincipal {
  /** Conteo de piezas (bloque contable o conteo del modelo IFC). */
  conteo?: number | null;
  /** Largo/corrido medido, en metros (capa 2D o longitud del IFC). En un muro con vanos, SIN los huecos. */
  largo?: number | null;
  /**
   * Muro con vanos: el largo CON los huecos (eje bruto = largo sin huecos + Σ ancho de los vanos), en
   * metros. Lo usa un complemento con `incluyeVanos=true` y `derivaDe='largo'`.
   */
  largoConVanos?: number | null;
  /** Área, en m² (recintos del 2D, cara del muro = largo×alto, o área del IFC). */
  area?: number | null;
  /** Volumen, en m³ (largo×alto×espesor del 2D o volumen del IFC). */
  volumen?: number | null;
  /**
   * Cuántas puertas y ventanas aloja el elemento (un muro con vanos: los que dibuja el plano o los que el
   * modelo IFC le aloja). Lo usa lo que va en cada vano (`derivaDe='vanos'`: refuerzo, dintel, cabezal).
   */
  vanos?: number | null;
}

export interface CantidadDerivadaResult {
  /** Cantidad derivada, o null = "a definir": la fuente no está medida (el usuario la pone a mano). */
  cantidad: number | null;
  /** true = la cantidad usa un supuesto (dimensión que el plano no dibuja) → el renglón va 'revisar'. */
  esSupuesto: boolean;
  /** true = salió del largo CON huecos (para decirlo en la explicación de la cantidad). */
  usaLargoConVanos: boolean;
}

/** Contrato de derivación que aporta la IA (subconjunto de PlanoComplementario). */
type ContratoDerivacion = Pick<
  PlanoComplementario,
  'derivaDe' | 'factor' | 'dimensionesFaltantes' | 'incluyeVanos'
>;

/** Un factor válido y positivo, o 1 (comportamiento previo: derivar 1:1 la magnitud). */
function factorValido(f: number | null | undefined): number {
  return typeof f === 'number' && Number.isFinite(f) && f > 0 ? f : 1;
}

/** ¿La IA supuso alguna dimensión que el plano no dibuja para llegar a esta cantidad? */
function tieneSupuesto(dims: ContratoDerivacion['dimensionesFaltantes']): boolean {
  return Array.isArray(dims) && dims.some((d) => d && typeof d.valorSugerido === 'number');
}

/**
 * Deriva la cantidad de un complementario. Devuelve `cantidad=null` ("a definir") cuando la
 * magnitud de origen no está medida (p. ej. `derivaDe='volumen'` pero el principal no tiene
 * volumen, o `derivaDe='otro'`): en ese caso NO se inventa un número —igual que hoy—.
 */
export function derivarCantidadComplementaria(
  comp: ContratoDerivacion,
  principal: MagnitudesPrincipal,
): CantidadDerivadaResult {
  const esSupuesto = tieneSupuesto(comp.dimensionesFaltantes);
  const factor = factorValido(comp.factor);

  let base: number | null;
  let usaLargoConVanos = false;
  switch (comp.derivaDe) {
    case 'conteo':
      base = numOrNull(principal.conteo);
      break;
    case 'largo': {
      // Con huecos solo si la IA lo pidió Y el elemento los tiene medidos; si no, el largo de siempre.
      const conVanos = comp.incluyeVanos === true ? numOrNull(principal.largoConVanos) : null;
      usaLargoConVanos = conVanos != null;
      base = conVanos ?? numOrNull(principal.largo);
      break;
    }
    case 'area':
      base = numOrNull(principal.area);
      break;
    case 'volumen':
      base = numOrNull(principal.volumen);
      break;
    case 'vanos':
      base = numOrNull(principal.vanos);
      break;
    case 'otro':
    default:
      base = null; // no derivable de la geometría → a definir
      break;
  }

  if (base == null) return { cantidad: null, esSupuesto, usaLargoConVanos: false };
  // 3 decimales cubren m³ (volumen); más allá es ruido para una cantidad de obra.
  const cantidad = Math.round(base * factor * 1000) / 1000;
  return { cantidad: cantidad > 0 ? cantidad : null, esSupuesto, usaLargoConVanos };
}

/** Un número finito y > 0, o null (una magnitud 0/ausente no deriva cantidad). */
function numOrNull(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * La cantidad derivada pasada por la medida que dice el propio concepto (un relleno «de 10 cm promedio»: del área al
 * volumen). Redondea a 3 decimales DESPUÉS de multiplicar, sobre la derivada ya redondeada: la misma cuenta en el
 * armado y al recalcular por «Cambió el plano» (redondear una sola vez da ±0.001 y saldría «revisar» de más).
 */
export function porMedidaDelConcepto(cantidadDerivada: number, medidaM: number): number {
  return Math.round(cantidadDerivada * medidaM * 1000) / 1000;
}
